import { Alert, Button, Container, Group, Table, Text, ThemeIcon } from '@mantine/core'
import { ContextModalProps } from '@mantine/modals'
import { IconCheck, IconMail } from '@tabler/icons-react'
import { useAccount } from 'hooks/auth'
import { useUpdateShortageOrder } from 'hooks/shortage'
import { useState } from 'react'
import { Manufacturer } from 'types/products'
import { ShortageOrderTableItem } from 'types/shortage'
import { generateMailToLink } from '../emails'
import { toastError } from '../notifications'

type Props = {
  selectedRows: Array<ShortageOrderTableItem>
}

export default function SendShortageOrderEmail({
  context,
  id,
  innerProps: { selectedRows },
}: ContextModalProps<Props>) {
  const { data: account } = useAccount()
  const [sentEmails, setSentEmails] = useState<Array<string>>([])
  const updateShortageOrder = useUpdateShortageOrder({ isEmailModal: true })

  const isAnyOrderEmailAlreadySent = selectedRows.some((row) => row.isEmailSent)
  const isAnyOrderAlreadyFulfilled = selectedRows.some((row) => row.fulfilledAt !== null)

  const manufacturers = selectedRows.reduce((acc, row) => {
    const manufacturer = row.manufacturer
    if (manufacturer && !acc.some((m) => m.name === manufacturer.name)) {
      acc.push(manufacturer)
    }
    return acc
  }, [] as Array<Manufacturer>)

  async function sendEmail(manufacturerId: string) {
    const manufacturer = manufacturers.find((m) => m.id === manufacturerId)
    if (manufacturer) {
      window.location.href = generateMailToLink({
        manufacturerEmail: manufacturer.email,
        fullName: `${account?.firstName} ${account?.lastName}`,
        orderProducts: selectedRows.map((row) => {
          return {
            symbol: row.product.symbol,
            size: row.size,
            amount: row.orderedAmount,
            unit: row.unit,
            feature: row.feature,
          }
        }),
      })
      setSentEmails((prev) => [...prev, manufacturer.name])
      selectedRows.forEach(async (row) => {
        if (row.manufacturer?.id !== manufacturerId) return
        await updateShortageOrder.mutateAsync({
          id: row.id,
          isEmailSent: true,
          fulfilledAmount: row.fulfilledAmount,
          fulfilledAt: row.fulfilledAt,
          orderedAmount: row.orderedAmount,
        })
      })
    } else {
      toastError('Nie znaleziono producenta.')
    }
  }

  return (
    <Container>
      <Text size="sm">
        {selectedRows.length === 0 &&
          `Nie zaznaczyłeś żadnych zamówień. Zaznacz zamówienia, dla których chcesz wysłać email z zamówieniem do producenta.`}
        {manufacturers.length > 1 &&
          `Zaznaczyłeś zamówienia z produktami od ${manufacturers.length} producentów. Wyślij email z zamówieniem oddzielnie dla każdego z nich. Kliknij w przycisk "Wyślij email" przy nazwie producenta, aby otworzyć domyślną aplikację pocztową z wygenerowanym zamówieniem.`}
        {manufacturers.length === 0 &&
          `Zaznaczyłeś zamówienia produktów, które nie mają przypisanego producenta. Nie można wysłać emaila z zamówieniem.`}
        {isAnyOrderEmailAlreadySent && (
          <Alert title="Uwaga" color="yellow" mt="md">
            Dla niektórych z zaznaczonych zamówień został już wysłany email z zamówieniem do producenta. Jeśli to
            pomyłka, zamknij to okno i odznacz odpowiednie zamówienia.
          </Alert>
        )}
        {isAnyOrderAlreadyFulfilled && (
          <Alert title="Uwaga" color="red" mt="md">
            Dla niektórych z zaznaczonych zamówień produkty zostały już dostarczone. Nie wysyłaj ponownie zamówienia do
            producenta.
          </Alert>
        )}
      </Text>
      <Table variant="vertical" withTableBorder mt="md">
        <Table.Tbody>
          {manufacturers.map((manufacturer) => {
            const isSentEmail = sentEmails.includes(manufacturer.name)
            return (
              <Table.Tr key={manufacturer.id}>
                <Table.Th w="50%">
                  <Group justify="space-between">
                    <Text>{manufacturer.name}</Text>
                    {isSentEmail && (
                      <ThemeIcon color="green" radius="xl" size="sm">
                        <IconCheck />
                      </ThemeIcon>
                    )}
                  </Group>
                </Table.Th>
                <Table.Td>
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconMail />}
                    onClick={() => sendEmail(manufacturer.id)}
                  >
                    {isSentEmail ? 'Wyślij ponownie' : 'Wyślij email'}
                  </Button>
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
      <Group mt="md" justify="flex-end">
        <Button variant="default" onClick={() => context.closeModal(id)}>
          Zamknij
        </Button>
      </Group>
    </Container>
  )
}
